// src/contexts/UserContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { getCurrentUser, isSessionValid, clearAuthData } from '../utils/auth';

const UserContext = createContext();

// User state management
const userReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false
      };
    case 'CLEAR_USER':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        cachedData: {},
        dataRefreshTrigger: 0
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    case 'UPDATE_CACHED_DATA':
      return {
        ...state,
        cachedData: {
          ...state.cachedData,
          [action.key]: action.payload
        }
      };
    case 'CLEAR_CACHED_DATA':
      return {
        ...state,
        cachedData: {}
      };
    case 'TRIGGER_DATA_REFRESH':
      return {
        ...state,
        dataRefreshTrigger: state.dataRefreshTrigger + 1
      };
    default:
      return state;
  }
};

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  cachedData: {},
  dataRefreshTrigger: 0
};

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Initialize user on mount
  useEffect(() => {
    const initializeUser = () => {
      const currentUser = getCurrentUser();
      
      if (currentUser && isSessionValid()) {
        dispatch({ type: 'SET_USER', payload: currentUser });
      } else {
        // Clear any stale data
        clearAuthData();
        dispatch({ type: 'CLEAR_USER' });
      }
    };

    initializeUser();
  }, []);

  // User authentication actions
  const login = (userData) => {
    // Clear any previous user data first
    dispatch({ type: 'CLEAR_USER' });
    dispatch({ type: 'CLEAR_CACHED_DATA' });
    
    // Set new user
    dispatch({ type: 'SET_USER', payload: userData });
  };

  const logout = () => {
    clearAuthData();
    dispatch({ type: 'CLEAR_USER' });
    dispatch({ type: 'CLEAR_CACHED_DATA' });
    
    // Force page reload to clear all React state
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  const updateCachedData = (key, data) => {
    // Only cache data for current user
    if (state.isAuthenticated && state.user) {
      const userKey = `${key}_${state.user.userId}`;
      dispatch({ type: 'UPDATE_CACHED_DATA', key: userKey, payload: data });
    }
  };

  const getCachedData = (key) => {
    if (state.isAuthenticated && state.user) {
      const userKey = `${key}_${state.user.userId}`;
      return state.cachedData[userKey] || null;
    }
    return null;
  };

  const triggerDataRefresh = () => {
    console.log('[UserContext] Triggering data refresh across components');
    dispatch({ type: 'TRIGGER_DATA_REFRESH' });
  };

  const value = {
    ...state,
    login,
    logout,
    updateCachedData,
    getCachedData,
    triggerDataRefresh,
    getCurrentUserId: () => state.user?.userId || null,
    isCurrentUser: (userId) => state.user?.userId === userId
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
